import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '@/types/notification';
import type { User } from '@/types/user';
import { notificationsService } from '@/lib/supabase/notifications';
import { profilesService } from '@/lib/supabase/profiles';
import { pushManager, getPushSupportInfo, registerServiceWorker } from '@/lib/push';
import { AuthContext } from './AuthContext';
import { logError } from '@/utils/error-handling';
import { NotificationToast } from '@/components/notifications/NotificationToast';
import { PushPermissionPrompt } from '@/components/notifications/PushPermissionPrompt';
import { useAppTourOptional } from '@/features/app-tour';

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  requestPushPermission: () => Promise<{ success: boolean; reason?: string }>;
  showToast: (title: string, message: string, type?: 'chat_message' | 'new_connection' | 'admin_message' | 'event_notification' | 'connection_request_received' | 'connection_request_sent' | 'connection_accepted') => void;
}

interface ToastItem {
  id: string;
  title: string;
  message: string;
  type?: 'chat_message' | 'new_connection' | 'admin_message' | 'event_notification' | 'connection_request_received' | 'connection_request_sent' | 'connection_accepted';
  url?: string;
}

// Centralizar el destino de notificaciones
const getNotificationDestinationUrl = (notification: Notification): string => {
  if (notification.type === 'chat_message') {
    // Abrir directamente la conversación con el remitente
    const senderId = notification.payload?.sender_id;
    if (senderId) {
      return `/chats?tab=chats&user=${senderId}`;
    }
    return '/chats?tab=chats';
  }
  if (notification.type === 'new_connection') {
    return '/chats?tab=actividad';
  }
  if (
    notification.type === 'connection_request_received' ||
    notification.type === 'connection_request_sent' ||
    notification.type === 'connection_accepted'
  ) {
    return '/chats?tab=actividad';
  }
  if (notification.type === 'event_notification' && notification.payload?.event_id) {
    return `/evento/${notification.payload.event_id}?tab=notificaciones`;
  }
  if (notification.type === 'admin_message') {
    return '/eventos';
  }
  return '/eventos';
};

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

const getNotificationCopy = (notification: Notification, senderProfiles?: Map<string, User>) => {
  if (notification.type === 'admin_message') {
    return {
      title: notification.payload?.title || 'Notificación del administrador',
      message: notification.payload?.message || 'Tienes una nueva notificación.',
    };
  }

  if (notification.type === 'chat_message') {
    const senderId = notification.payload?.sender_id;
    if (senderId && senderProfiles) {
      const sender = senderProfiles.get(senderId);
      if (sender) {
        const senderName = sender.name || sender.firstName || 'Alguien';
        return {
          title: 'Nuevo mensaje',
          message: `${senderName} te ha enviado un mensaje.`,
        };
      }
    }
    return {
      title: 'Nuevo mensaje',
      message: 'Alguien te ha enviado un mensaje.',
    };
  }

  if (notification.type === 'new_connection') {
    const senderId = notification.payload?.sender_id;
    if (senderId && senderProfiles) {
      const sender = senderProfiles.get(senderId);
      if (sender) {
        const senderName = sender.name || sender.firstName || 'Alguien';
        return {
          title: 'Nueva conexión',
          message: `${senderName} quiere conectar contigo.`,
        };
      }
    }
    return {
      title: 'Nueva conexión',
      message: 'Alguien quiere conectar contigo.',
    };
  }

  if (notification.type === 'event_notification') {
    return {
      title: notification.payload?.title || 'Notificación del evento',
      message: notification.payload?.message || 'Tienes una nueva notificación del evento.',
    };
  }

  if (notification.type === 'connection_request_received') {
    const senderId = notification.payload?.sender_id;
    if (senderId && senderProfiles) {
      const sender = senderProfiles.get(senderId);
      const name = sender?.name || sender?.firstName || 'Alguien';
      return { title: 'Solicitud de conexión', message: `${name} te ha enviado una solicitud.` };
    }
    return { title: 'Solicitud de conexión', message: 'Tienes una nueva solicitud de conexión.' };
  }
  if (notification.type === 'connection_request_sent') {
    return { title: 'Solicitud enviada', message: 'Tu solicitud de conexión ha sido enviada.' };
  }
  if (notification.type === 'connection_accepted') {
    const senderId = notification.payload?.sender_id;
    if (senderId && senderProfiles) {
      const sender = senderProfiles.get(senderId);
      const name = sender?.name || sender?.firstName || 'Alguien';
      return { title: 'Conexión aceptada', message: `${name} ha aceptado tu solicitud.` };
    }
    return { title: 'Conexión aceptada', message: 'Tu solicitud de conexión ha sido aceptada.' };
  }

  return {
    title: 'Nueva conexión',
    message: 'Alguien quiere conectar contigo.',
  };
};

export function NotificationsProvider({ children }: { children: ReactNode }) {
  // Usar useContext directamente para manejar el caso donde el contexto aún no está disponible
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const currentUserProfile = authContext?.currentUserProfile ?? null;
  const navigate = useNavigate();
  const appTour = useAppTourOptional();
  const shouldDeferSecondaryPrompts = appTour?.shouldDeferSecondaryPrompts ?? false;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const senderProfilesRef = useRef<Map<string, User>>(new Map());

  // Registrar service worker temprano (especialmente importante para iOS PWA)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      const supportInfo = getPushSupportInfo();
      // Registrar siempre, pero especialmente importante para iOS PWA
      if (supportInfo.hasServiceWorker) {
        registerServiceWorker().catch(() => {
          // Error silencioso al registrar service worker
        });
      }
    }
  }, []);

  // Efecto para mostrar el prompt de permisos después de 3 segundos
  useEffect(() => {
    if (!isAuthenticated || !currentUserProfile?.id) {
      return;
    }

    if (shouldDeferSecondaryPrompts) {
      return;
    }

    // Verificar si ya se mostró el prompt antes
    const hasShownPrompt = localStorage.getItem('push-prompt-shown');

    if (hasShownPrompt) {
      return;
    }

    // Verificar soporte de push
    const supportInfo = getPushSupportInfo();

    // Mostrar el prompt incluso si requiere PWA (para informar al usuario)
    if (typeof window !== 'undefined' && ('Notification' in window || supportInfo.requiresPWA)) {
      // Verificar permisos de forma segura
      const canAsk =
        ('Notification' in window && Notification.permission === 'default') ||
        supportInfo.requiresPWA;

      if (canAsk) {
        const timer = setTimeout(() => {
          setShowPushPrompt(true);
          localStorage.setItem('push-prompt-shown', 'true');
        }, 3000); // Esperar 3 segundos después de autenticarse

        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, currentUserProfile?.id, shouldDeferSecondaryPrompts]);

  useEffect(() => {
    if (!isAuthenticated || !currentUserProfile?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    const userId = currentUserProfile.id;
    let unsubscribe: (() => void) | null = null;

    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const [latestNotifications, unread] = await Promise.all([
          notificationsService.getNotifications(userId, 25),
          notificationsService.getUnreadCount(userId),
        ]);

        if (!isMounted) return;
        setNotifications(latestNotifications);
        setUnreadCount(unread);

        // Cargar perfiles de usuarios para notificaciones de chat y conexión
        const senderIds = new Set<string>();
        latestNotifications.forEach((notification) => {
          if ((notification.type === 'chat_message' || notification.type === 'new_connection')
            && notification.payload?.sender_id) {
            senderIds.add(notification.payload.sender_id);
          }
        });

        if (senderIds.size > 0) {
          try {
            const users = await profilesService.getUsersByIds(Array.from(senderIds));
            const profilesMap = new Map<string, User>();
            users.forEach((user) => {
              profilesMap.set(user.id, user);
            });
            if (isMounted) {
              senderProfilesRef.current = profilesMap;
            }
          } catch (error) {
            logError(error, 'NotificationsProvider.loadProfiles', true);
          }
        }
      } catch (error) {
        logError(error, 'NotificationsProvider.loadInitial', true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const enqueueToast = async (notification: Notification) => {
      // Si es una notificación de chat o conexión, cargar el perfil del remitente si no está en caché
      let profiles = senderProfilesRef.current;
      if ((notification.type === 'chat_message' || notification.type === 'new_connection')
        && notification.payload?.sender_id) {
        const senderId = notification.payload.sender_id;
        if (!profiles.has(senderId)) {
          try {
            const user = await profilesService.getUserById(senderId);
            if (user) {
              const newProfiles = new Map(profiles);
              newProfiles.set(senderId, user);
              senderProfilesRef.current = newProfiles;
              profiles = newProfiles;
            }
          } catch (error) {
            logError(error, 'NotificationsProvider.enqueueToast.loadProfile', true);
          }
        }
      }

      const { title, message } = getNotificationCopy(notification, profiles);
      const toastId = `${notification.id}-${Date.now()}`;
      const url = getNotificationDestinationUrl(notification);
      setToasts((prev) => [...prev, {
        id: toastId,
        title,
        message,
        type: notification.type,
        url
      }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
      }, 5000);
    };

    loadInitialData();

    unsubscribe = notificationsService.subscribe(
      userId,
      async (notification) => {
        setNotifications((prev) => [notification, ...prev].slice(0, 50));
        setUnreadCount((count) => count + 1);

        // Cargar perfil del remitente si es una notificación de chat o conexión
        let profiles = senderProfilesRef.current;
        if ((notification.type === 'chat_message' || notification.type === 'new_connection')
          && notification.payload?.sender_id) {
          const senderId = notification.payload.sender_id;
          if (!profiles.has(senderId)) {
            try {
              const user = await profilesService.getUserById(senderId);
              if (user) {
                const newProfiles = new Map(profiles);
                newProfiles.set(senderId, user);
                senderProfilesRef.current = newProfiles;
                profiles = newProfiles;
              }
            } catch (error) {
              logError(error, 'NotificationsProvider.subscribe.loadProfile', true);
            }
          }
        }

        await enqueueToast(notification);
        const copy = getNotificationCopy(notification, profiles);
        const pushUrl = getNotificationDestinationUrl(notification);
        pushManager.showLocalNotification({
          title: copy.title,
          body: copy.message,
          url: pushUrl,
        });
      },
      (error) => logError(error, 'NotificationsProvider.subscribe', true)
    );

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [isAuthenticated, currentUserProfile?.id]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const success = await notificationsService.markAsRead(notificationId);
      if (success) {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, read_at: new Date().toISOString() }
              : notification
          )
        );
        setUnreadCount((count) => Math.max(0, count - 1));
      }
    } catch (error) {
      logError(error, 'NotificationsProvider.markAsRead', true);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!currentUserProfile?.id) return;
    try {
      const unreadNotifications = notifications.filter((notification) => !notification.read_at);
      if (unreadNotifications.length === 0) return;

      const success = await notificationsService.markAllAsRead(currentUserProfile.id);
      if (success) {
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
        return;
      }

      // Fallback defensivo: si el update masivo falla, intentar una a una.
      const settled = await Promise.allSettled(
        unreadNotifications.map((notification) => notificationsService.markAsRead(notification.id))
      );
      const markedCount = settled.filter((r) => r.status === 'fulfilled' && r.value).length;
      if (markedCount > 0) {
        const nowIso = new Date().toISOString();
        const unreadIds = new Set(unreadNotifications.map((n) => n.id));
        setNotifications((prev) =>
          prev.map((notification) =>
            unreadIds.has(notification.id)
              ? { ...notification, read_at: notification.read_at ?? nowIso }
              : notification
          )
        );
        setUnreadCount((count) => Math.max(0, count - markedCount));
      }
    } catch (error) {
      logError(error, 'NotificationsProvider.markAllAsRead', true);
    }
  }, [currentUserProfile?.id, notifications]);

  const requestPushPermission = useCallback(async () => {
    if (!currentUserProfile?.id) {
      return { success: false as const, reason: 'missing_user' };
    }
    return pushManager.register(currentUserProfile.id);
  }, [currentUserProfile?.id]);

  const showToast = useCallback((title: string, message: string, type?: 'chat_message' | 'new_connection' | 'admin_message' | 'event_notification' | 'connection_request_received' | 'connection_request_sent' | 'connection_accepted') => {
    const toastId = `manual-${Date.now()}`;
    setToasts((prev) => [...prev, {
      id: toastId,
      title,
      message,
      type: type || 'admin_message'
    }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
    }, 5000);
  }, []);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      markAsRead,
      markAllAsRead,
      requestPushPermission,
      showToast,
    }),
    [notifications, unreadCount, isLoading, markAsRead, markAllAsRead, requestPushPermission, showToast]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <div className="notification-toast-stack">
        {toasts.map((toast) => (
          <NotificationToast
            key={toast.id}
            title={toast.title}
            message={toast.message}
            type={toast.type}
            onClose={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
            onClick={toast.url ? () => navigate(toast.url!) : undefined}
          />
        ))}
      </div>
      {showPushPrompt && (
        <PushPermissionPrompt
          onRequest={requestPushPermission}
          onDismiss={() => setShowPushPrompt(false)}
        />
      )}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications debe usarse dentro de NotificationsProvider');
  }
  return context;
}

/**
 * Hook opcional para usar notificaciones sin lanzar errores si el contexto no está disponible
 * Útil para componentes que pueden renderizarse fuera del NotificationsProvider
 */
export function useNotificationsOptional(): NotificationsContextValue | null {
  const context = useContext(NotificationsContext);
  return context || null;
}

